import { ReactElement, useMemo } from 'react'

import { QuotesListProvider, QuotesListProviderProps } from './context/quotesList'
import { QuotesListFiltersProvider, QuotesListFiltersProviderProps } from './context/quotesListFilters'
import { QuotesListWithSummary } from './elements/QuotesListWithSummary'

type QuotesListOptions = QuotesListProviderProps['options']

interface QuotesListProps {
  columns?: QuotesListProviderProps['columns']
  contextFilters?: QuotesListFiltersProviderProps['contextFilters']
  // When `false`, the list mounts but skips data fetching so the table renders its natural empty
  // state. Use for customer-scoped instances whose customerId may not be resolved yet.
  isFetchEnabled?: QuotesListOptions['isFetchEnabled']
  newDocumentUrl: QuotesListProviderProps['newDocumentUrl']
  persistFiltersInUrl?: QuotesListOptions['persistFiltersInUrl']
  showPeriodSelector?: QuotesListOptions['showPeriodSelector']
  showSearch?: QuotesListOptions['showSearch']
  showSummary?: QuotesListOptions['showSummary']
  showTitle?: QuotesListOptions['showTitle']
  tableId: QuotesListProviderProps['tableId']
}

export const QuotesList = ({
  columns,
  contextFilters,
  isFetchEnabled = true,
  newDocumentUrl,
  persistFiltersInUrl = true,
  showPeriodSelector = true,
  showSearch = true,
  showSummary = true,
  showTitle = true,
  tableId,
}: QuotesListProps): ReactElement => {
  const options = useMemo(
    () => ({
      isFetchEnabled,
      persistFiltersInUrl,
      showPeriodSelector,
      showSearch,
      showSummary,
      showTitle,
    }),
    [isFetchEnabled, persistFiltersInUrl, showPeriodSelector, showSearch, showSummary, showTitle],
  )

  return (
    <QuotesListProvider columns={columns} newDocumentUrl={newDocumentUrl} options={options} tableId={tableId}>
      <QuotesListFiltersProvider contextFilters={contextFilters}>
        <QuotesListWithSummary />
      </QuotesListFiltersProvider>
    </QuotesListProvider>
  )
}
